import {IAfterGuiAttachedParams, IDoesFilterPassParams, IFilterParams} from "@ag-grid-community/core";
import {Button, Intent} from "@blueprintjs/core";
import {DateRange, DateRangeShortcut} from "@blueprintjs/datetime";
import {DateInput3, DateRangeInput3} from "@blueprintjs/datetime2";
import styled from "@emotion/styled";
import {addDays, endOfDay, startOfDay, subDays} from "date-fns";
import {format} from "date-fns-tz";
import {forwardRef, useCallback, useEffect, useImperativeHandle, useState} from "react";
import {isValidDateTime} from "../../utils/DateTimeUtils";
import {getArrayFromField} from "../../utils/General";
import {getDateTimeByType, StopDateTimeType} from "./cell-renderers/DateTimeCellRenderer";
import {isString} from "lodash";
import {isCustomOption, TimeOption} from "./time-filter-select/TimeQuickFilterSelect";

const getDefaultShortcuts = () => {
  const today = new Date();
  return [
    {label: "Today", dateRange: [startOfDay(today), endOfDay(today)]},
    {label: "Yesterday", dateRange: [startOfDay(subDays(today, 1)), endOfDay(subDays(today, 1))]},
    {label: "Last 7 days", dateRange: [startOfDay(subDays(today, 6)), endOfDay(today)]},
    {label: "Last 14 days", dateRange: [startOfDay(subDays(today, 13)), endOfDay(today)]},
    {label: "Tomorrow", dateRange: [startOfDay(addDays(today, 1)), endOfDay(addDays(today, 1))]},
    {label: "Next 2 days", dateRange: [startOfDay(addDays(today, 1)), endOfDay(addDays(today, 2))]},
    {label: "Next 7 days", dateRange: [startOfDay(addDays(today, 1)), endOfDay(addDays(today, 7))]},
    {label: "Next 14 days", dateRange: [startOfDay(addDays(today, 1)), endOfDay(addDays(today, 14))]}
  ] as DateRangeShortcut[];
};

function getDateRangeByShortcutLabel(label: string): DateRange {
  return getDefaultShortcuts().find((item) => item.label === label)?.dateRange as DateRange;
}

const DefaultShortcutsTitles = getDefaultShortcuts().map((item) => item.label);

export const getRangeByOption = (option: TimeOption) => {
  if (!option) {
    return [null, null];
  }
  if (DefaultShortcutsTitles.includes(option.title)) {
    return getDefaultShortcuts().find((item) => item.label === option.title)?.dateRange as DateRange;
  } else if (option.getFilterValue) {
    return isCustomOption(option) ? option.getFilterValue(option.customValue) : option.getFilterValue();
  }
  return [null, null];
};

const Title = styled.div`
  font-weight: bold;
  padding: 0px 0px 10px 5px;
`;

const Footer = styled.div`
  padding-top: 10px;
`;

const DateInputContainer = styled.div``;

type DateFilterType = "single" | "range";
// eslint-disable-next-line react/display-name
export default forwardRef((props: IFilterParams, ref) => {
  const [init, setInit] = useState<boolean>(false);
  const [dateValue, setDateValue] = useState<string>("");
  const [range, setRange] = useState<DateRange>([null, null]);
  const [selectedOption, setSelectedOption] = useState<TimeOption | undefined>(undefined);
  const [shortcuts, setShortcuts] = useState<DateRangeShortcut[]>([]);

  const [afterGuiAttachedParams, setAfterGuiAttachedParams] = useState<IAfterGuiAttachedParams>();

  const dateRangeShortcuts: DateRangeShortcut[] = props.colDef.filterParams.dateRangeShortcuts;

  const isClientSideRangeFilter = props.colDef.filterParams.isClientSideRangeFilter;

  const getShortcuts = useCallback(() => {
    if (dateRangeShortcuts) {
      return dateRangeShortcuts;
    }
    return getDefaultShortcuts();
  }, [dateRangeShortcuts]);

  const getFilterType = () => {
    return props.colDef.filterParams.type as DateFilterType;
  };

  const getTypeOfDateTime = (): StopDateTimeType => {
    return props.colDef.filterParams.typeOfDateTime;
  };

  const onChange = (event: any) => {
    if (getFilterType() === "single") {
      setDateValue(event);
    } else {
      setRange([event[0] ? startOfDay(event[0]) : null, event[1] ? endOfDay(event[1]) : null]);
    }
  };

  const onApply = () => {
    if (getFilterType() === "range" && !getArrayFromField(range).every(isValidDateTime)) {
      setRange([
        isValidDateTime(range[0]) ? range[0] : new Date("Invalid Date"),
        isValidDateTime(range[1]) ? range[1] : new Date("Invalid Date")
      ]);
      return;
    }
    props.filterChangedCallback();
    afterGuiAttachedParams?.hidePopup?.();
  };

  const onClear = () => {
    if (getFilterType() === "single") {
      setDateValue("");
    } else {
      setSelectedOption(undefined);
      setRange([null, null]);
    }
    afterGuiAttachedParams?.hidePopup?.();
  };

  const handleShortcutsClicked = useCallback((e) => {
    if (DefaultShortcutsTitles.includes(e?.target?.innerText)) {
      const option: TimeOption = {
        title: e.target.innerText,
        getFilterValue: () => getDateRangeByShortcutLabel(e.target.innerText)
      };
      setSelectedOption(option);
    }
  }, []);

  const handleDayClicked = useCallback(() => {
    setSelectedOption(undefined);
  }, []);

  const handlePopoverOpening = useCallback(() => {
    setShortcuts(getShortcuts());
  }, [getShortcuts]);

  const handlePopoverOpened = useCallback(
    (container: HTMLElement) => {
      const shorcutEls = container.querySelectorAll(".bp5-menu-item");
      shorcutEls.forEach((node) => {
        if ((node as HTMLAnchorElement).innerText === selectedOption?.title) {
          node.classList.add("bp5-active", "bp5-selected");
        } else {
          node.classList.remove("bp5-active", "bp5-selected");
        }
      });
    },
    [selectedOption]
  );

  useImperativeHandle(ref, () => {
    setInit(true);
    const isFilterActiveLocal = () => (getFilterType() === "single" ? dateValue !== "" : !range?.includes(null));
    return {
      doesFilterPass(params: IDoesFilterPassParams) {
        if (isClientSideRangeFilter && getFilterType() === "range") {
          const dateTime = getDateTimeByType(params.data, getTypeOfDateTime());
          if (!dateTime) return false;
          if (range[0] === null || range[1] === null) return false;
          return range[0].getTime() <= dateTime.getTime() && dateTime.getTime() <= range[1].getTime();
        } else {
          return getFilterType() === "single" ? dateValue !== null : !range?.includes(null);
        }
      },

      isFilterActive() {
        return isFilterActiveLocal();
      },
      getModel() {
        if (!isFilterActiveLocal()) {
          return null;
        }
        const model = {filterType: "date"};
        if (isClientSideRangeFilter && getFilterType() === "range") {
          if (selectedOption) {
            const shortcutRange = getRangeByOption(selectedOption);
            return {
              ...model,
              dateFrom: shortcutRange?.[0] ? format(shortcutRange[0], "yyyy-MM-dd 00:00:00") : null,
              dateTo: shortcutRange?.[1] ? format(shortcutRange[1], "yyyy-MM-dd 23:59:59") : null,
              type: "inRange",
              value: selectedOption.title
            };
          }
          const dateFrom = range?.[0] ? format(range[0], "yyyy-MM-dd 00:00:00") : null;
          const dateTo = range?.[1] ? format(range[1], "yyyy-MM-dd 23:59:59") : null;
          return {
            ...model,
            dateFrom,
            dateTo,
            type: "inRange"
          };
        } else {
          if (getFilterType() === "single") {
            return {...model, type: "equals", value: dateValue};
          } else if (getFilterType() === "range") {
            if (selectedOption) {
              const dateRange = getRangeByOption(selectedOption);
              return {
                ...model,
                type: "inRange",
                filter: dateRange?.[0],
                filterTo: dateRange?.[1],
                value: selectedOption.title,
                option: selectedOption
              };
            }
            return {...model, type: "inRange", filter: range?.[0], filterTo: range?.[1]};
          }
        }
      },
      setModel(model: any) {
        if (model?.type === "equals") {
          setDateValue(model.value);
        } else if (model?.type === "inRange") {
          if (isString(model?.value)) {
            let option: TimeOption = model?.option;
            if (DefaultShortcutsTitles.includes(model?.value)) {
              option = {
                title: model.value,
                getFilterValue: () => getDateRangeByShortcutLabel(model.value)
              };
            }
            setSelectedOption(option);
            const selectedRange = getRangeByOption(option);
            setRange([selectedRange[0], selectedRange[1]]);
          } else if (model.filter && model.filterTo) {
            setRange([new Date(model.filter), new Date(model.filterTo)]);
          } else if (model.dateFrom && model.dateTo) {
            setRange([new Date(model.dateFrom), new Date(model.dateTo)]);
          } else {
            setRange(model.value);
          }
        } else {
          setDateValue("");
          setRange([null, null]);
        }
      },

      afterGuiAttached(params: IAfterGuiAttachedParams) {
        setAfterGuiAttachedParams(params);
      }
    };
  });

  const formatDate = (date: Date) => format(date, "MM/dd/yyy");
  const parseDate = (str: string) => {
    // automatically add the splashes
    const cleaned = str.replace(/\D/g, "").slice(0, 8);
    let newValue = cleaned;
    if (cleaned.length >= 5) {
      newValue = `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 3) {
      newValue = `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return new Date(newValue);
  };

  const getDateComponent = () => {
    const commonProps = {
      className: "ag-custom-component-popup",
      fill: true,
      highlightCurrentDay: true,
      closeOnSelection: true,
      singleMonthOnly: true
    };
    if (getFilterType() === "single") {
      return (
        <DateInput3
          {...commonProps}
          value={dateValue}
          onChange={onChange}
          formatDate={formatDate}
          parseDate={parseDate}
        />
      );
    } else {
      return (
        <DateRangeInput3
          {...commonProps}
          value={range}
          onChange={onChange}
          formatDate={formatDate}
          parseDate={parseDate}
          shortcuts={shortcuts}
          allowSingleDayRange
          dayPickerProps={{
            onDayClick: handleDayClicked
          }}
          popoverProps={{onOpening: handlePopoverOpening, onOpened: handlePopoverOpened}}
        />
      );
    }
  };

  useEffect(() => {
    if (init && dateValue === "" && range?.every((x: Date | null) => x === null)) {
      props.filterChangedCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue, range]);

  return (
    <div style={{padding: 10, width: 250}} data-testid={"date-filter"}>
      <Title>{getFilterType() === "single" ? "Date" : "Date Range"}</Title>
      <DateInputContainer onClick={handleShortcutsClicked}>{getDateComponent()}</DateInputContainer>
      <Footer>
        <Button text={"Apply"} style={{marginRight: "5px"}} intent={Intent.PRIMARY} onClick={onApply} />
        <Button text={"Reset"} style={{marginRight: "5px"}} onClick={onClear} />
      </Footer>
    </div>
  );
});
